import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Search, Plus, FileText, Trash2, Copy, X } from '../icons';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../components/Modal';
import {
  getContracts, createContract, deleteContract, duplicateContract,
  getContractTemplates, deleteContractTemplate,
} from '../services/api';

export const CONTRACT_STATUS = {
  draft: 'Draft',
  awaiting_signature: 'Awaiting signature',
  countersign: 'Countersign',
  completed: 'Completed',
  canceled: 'Canceled',
};

const TABS = [
  { key: 'all', label: 'All Contracts' },
  { key: 'draft', label: 'Draft' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'completed', label: 'Completed' },
  { key: 'canceled', label: 'Canceled' },
];

export const errMsg = (err, fallback) => err?.response?.data?.error || fallback;

const initials = (name) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

function StudioContracts() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [confirmDialog, ConfirmDialog] = useConfirm();
  const [view, setView] = useState('contracts');
  const [tab, setTab] = useState('all');
  const [query, setQuery] = useState('');
  const [contracts, setContracts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') navigate('/login');
  }, [user, authLoading, navigate]);

  const loadContracts = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await getContracts({ status: tab, q: query.trim() || undefined });
      setContracts(res.data);
    } catch (err) {
      setLoadError(errMsg(err, 'Could not load contracts'));
    } finally {
      setLoading(false);
    }
  }, [tab, query]);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await getContractTemplates();
      setTemplates(res.data);
    } catch (err) {
      toast.error(errMsg(err, 'Could not load templates'));
    }
  }, []);

  useEffect(() => {
    if (!user || user.role !== 'admin' || view !== 'contracts') return undefined;
    const t = setTimeout(loadContracts, query ? 250 : 0);
    return () => clearTimeout(t);
  }, [user, view, loadContracts, query]);

  useEffect(() => {
    if (user?.role === 'admin') loadTemplates();
  }, [user, loadTemplates]);

  const handleDelete = async (c) => {
    const ok = await confirmDialog({
      title: 'Delete contract',
      message: `Delete "${c.name}"? This cannot be undone.`,
      danger: true,
      confirmText: 'Delete',
    });
    if (!ok) return;
    try {
      await deleteContract(c.id);
      toast.success('Contract deleted');
      loadContracts();
    } catch (err) {
      toast.error(errMsg(err, 'Could not delete contract'));
    }
  };

  const handleDuplicate = async (c) => {
    try {
      const res = await duplicateContract(c.id);
      toast.success('Contract duplicated');
      navigate(`/admin/studio/contracts/${res.data.id}`);
    } catch (err) {
      toast.error(errMsg(err, 'Could not duplicate'));
    }
  };

  const handleDeleteTemplate = async (t) => {
    const ok = await confirmDialog({
      title: 'Delete template',
      message: `Delete the template "${t.name}"? Contracts already created from it are not affected.`,
      danger: true,
      confirmText: 'Delete',
    });
    if (!ok) return;
    try {
      await deleteContractTemplate(t.id);
      toast.success('Template deleted');
      loadTemplates();
    } catch (err) {
      toast.error(errMsg(err, 'Could not delete template'));
    }
  };

  if (authLoading || !user || user.role !== 'admin') return null;

  return (
    <div className="admin-page">
      <div className="admin-container sc-page">
        <button className="sc-back" onClick={() => navigate('/admin')}>
          <ArrowLeft size={16} /> Admin
        </button>

        <div className="sc-header">
          <div>
            <h1>{view === 'contracts' ? 'Contracts' : 'Contract Templates'}</h1>
            <p>Studio &rsaquo; Documents</p>
          </div>
          <div className="sc-header-actions">
            {view === 'contracts' && (
              <div className="admin-search sc-search">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search email or contact name"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            )}
            <button className="btn btn-outline btn-sm" onClick={() => setView(view === 'contracts' ? 'templates' : 'contracts')}>
              {view === 'contracts' ? 'View Templates' : 'Back to Contracts'}
            </button>
            {view === 'contracts' ? (
              <button className="btn btn-primary btn-sm" onClick={() => setShowNew(true)}>
                <Plus size={14} /> New Contract
              </button>
            ) : (
              <button className="btn btn-primary btn-sm" onClick={() => navigate('/admin/studio/templates/new')}>
                <Plus size={14} /> New Template
              </button>
            )}
          </div>
        </div>

        {view === 'contracts' && (
          <>
            <div className="sc-tabs">
              {TABS.map((t) => (
                <button key={t.key} className={`sc-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
                  {t.label}
                </button>
              ))}
            </div>

            <div className="sc-card">
              {loadError ? (
                <div className="sc-empty">
                  <p>{loadError}</p>
                  <button className="btn btn-outline btn-sm" onClick={loadContracts}>Try again</button>
                </div>
              ) : loading ? (
                <div className="sc-empty"><p>Loading…</p></div>
              ) : contracts.length === 0 ? (
                <div className="sc-empty">
                  <FileText size={32} />
                  <p>{query || tab !== 'all' ? 'No contracts match.' : 'No contracts yet. Create one to send to a client.'}</p>
                </div>
              ) : (
                <table className="sc-table">
                  <thead>
                    <tr><th>Name</th><th>Client</th><th>Project</th><th>Created</th><th /></tr>
                  </thead>
                  <tbody>
                    {contracts.map((c) => (
                      <tr key={c.id} onClick={() => navigate(`/admin/studio/contracts/${c.id}`)}>
                        <td>
                          <span className="sc-name">{c.name}</span>
                          <span className={`sc-badge sc-${c.status}`}>{CONTRACT_STATUS[c.status]}</span>
                        </td>
                        <td>
                          <span className="sc-client">
                            <span className="sc-avatar">{initials(c.client_name)}</span>
                            <span>
                              {c.client_name}
                              <small>{c.client_email}</small>
                            </span>
                          </span>
                        </td>
                        <td>{c.project_name || '—'}</td>
                        <td>{fmtDate(c.created_at)}</td>
                        <td className="sc-row-actions">
                          <button
                            className="sc-icon-btn sc-icon-neutral"
                            title="Duplicate"
                            onClick={(e) => { e.stopPropagation(); handleDuplicate(c); }}
                          >
                            <Copy size={16} />
                          </button>
                          {(c.status === 'draft' || c.status === 'canceled') && (
                            <button
                              className="sc-icon-btn"
                              title="Delete"
                              onClick={(e) => { e.stopPropagation(); handleDelete(c); }}
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {!loading && !loadError && contracts.length > 0 && (
                <div className="sc-count">{contracts.length} contract{contracts.length === 1 ? '' : 's'}</div>
              )}
            </div>
          </>
        )}

        {view === 'templates' && (
          <div className="sc-card">
            {templates.length === 0 ? (
              <div className="sc-empty"><p>No templates yet.</p></div>
            ) : (
              <table className="sc-table">
                <thead><tr><th>Template</th><th>Description</th><th>Updated</th><th /></tr></thead>
                <tbody>
                  {templates.map((t) => (
                    <tr key={t.id} onClick={() => navigate(`/admin/studio/templates/${t.id}`)}>
                      <td><span className="sc-name">{t.name}</span></td>
                      <td>{t.description || '—'}</td>
                      <td>{fmtDate(t.updated_at)}</td>
                      <td className="sc-row-actions">
                        <button
                          className="sc-icon-btn"
                          title="Delete"
                          onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(t); }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {showNew && (
        <NewContractModal
          templates={templates}
          onClose={() => setShowNew(false)}
          onCreated={(c) => navigate(`/admin/studio/contracts/${c.id}`)}
        />
      )}
      {ConfirmDialog}
    </div>
  );
}

function NewContractModal({ templates, onClose, onCreated }) {
  const [form, setForm] = useState({ client_name: '', client_email: '', project_name: '', event_date: '', template_id: '' });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!form.template_id && templates.length > 0) {
      setForm((f) => ({ ...f, template_id: String(templates.find((t) => t.is_default)?.id || templates[0].id) }));
    }
  }, [templates, form.template_id]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await createContract({
        ...form,
        template_id: form.template_id || undefined,
        event_date: form.event_date || undefined,
      });
      onCreated(res.data);
    } catch (err) {
      toast.error(errMsg(err, 'Could not create contract'));
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal-content sc-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        <h3 className="modal-title">New Contract</h3>
        <label className="sc-field">
          <span>Client name</span>
          <input required value={form.client_name} onChange={set('client_name')} placeholder="Jane Doe" />
        </label>
        <label className="sc-field">
          <span>Client email</span>
          <input required type="email" value={form.client_email} onChange={set('client_email')} placeholder="jane@example.com" />
        </label>
        <label className="sc-field">
          <span>Project name</span>
          <input value={form.project_name} onChange={set('project_name')} placeholder="Jane & John's Wedding" />
        </label>
        <label className="sc-field">
          <span>Event date</span>
          <input type="date" value={form.event_date} onChange={set('event_date')} />
        </label>
        <label className="sc-field">
          <span>Template</span>
          <select value={form.template_id} onChange={set('template_id')}>
            <option value="">Blank contract</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </label>
        <div className="modal-actions">
          <button type="button" className="btn btn-outline modal-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary modal-btn" disabled={saving}>{saving ? 'Creating…' : 'Create'}</button>
        </div>
      </form>
    </div>
  );
}

export default StudioContracts;
